export type BuddyKind = 'A' | 'I';
export type BuddyMouth = 'smile' | 'grin' | 'o' | 'flat' | 'effort' | 'wobble';
type Point = readonly [number, number];

export interface BuddyArm {
  side: 1 | -1;
  hand: Point;
}

export interface BuddyPose {
  kind: BuddyKind;
  cx: number;
  cy: number;
  rot: number;
  sx: number;
  sy: number;
  look: Point;
  open: number;
  squint: number;
  dizzy: boolean;
  mouth: BuddyMouth;
  blush: number;
  arms: BuddyArm[];
}

export interface BuddyColors {
  ink: string;
  face: string;
  blush: string;
  shadow: string;
  spark: string;
  accent: string;
  muted: string;
}

export type BuddyEffect = (g: CanvasRenderingContext2D, colors: BuddyColors) => void;

export const BUDDY_HEIGHT = 22.26;
const H2 = BUDDY_HEIGHT / 2;

const OUTLINES: Record<BuddyKind, readonly Point[]> = {
  A: [
    [-9.65, H2],
    [-2.75, -H2],
    [2.75, -H2],
    [9.65, H2],
  ],
  I: [
    [-4.7, H2],
    [-4.7, -H2],
    [4.7, -H2],
    [4.7, H2],
  ],
};
const CORNER_RADIUS: Record<BuddyKind, number> = { A: 1.1, I: 1 };
const EYES: Record<BuddyKind, readonly Point[]> = {
  A: [
    [-1.7, -1.43],
    [1.7, -1.43],
  ],
  I: [
    [-1.9, -3.43],
    [1.9, -3.43],
  ],
};
const MOUTH: Record<BuddyKind, Point> = { A: [0, 1.5], I: [0, -0.5] };
const SHOULDER: Record<BuddyKind, Point> = { A: [6.2, 3.1], I: [4, 1] };
export const BUDDY_HALF_WIDTH: Record<BuddyKind, number> = { A: 9.65, I: 4.7 };

const FULL_TURN = Math.PI * 2;

export const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
export const segment = (t: number, from: number, to: number): number => clamp01((t - from) / (to - from));
export const easeBack = (u: number): number => {
  const c1 = 1.70158;
  return 1 + (c1 + 1) * (u - 1) ** 3 + c1 * (u - 1) ** 2;
};
export const easeInOutCubic = (u: number): number => (u < 0.5 ? 4 * u ** 3 : 1 - (-2 * u + 2) ** 3 / 2);

export const freshPose = (kind: BuddyKind): BuddyPose => ({
  kind,
  cx: 0,
  cy: -H2,
  rot: 0,
  sx: 1,
  sy: 1,
  look: [0, 0],
  open: 1,
  squint: 0,
  dizzy: false,
  mouth: 'smile',
  blush: 0,
  arms: [],
});

export const toWorld = (c: BuddyPose, p: Point): Point => {
  const x = p[0] * c.sx;
  const y = (p[1] - H2) * c.sy + H2;
  const cos = Math.cos(c.rot);
  const sin = Math.sin(c.rot);
  return [c.cx + x * cos - y * sin, c.cy + x * sin + y * cos];
};

export const topOf = (c: BuddyPose): Point => toWorld(c, [0, -H2]);

export const raiseArm = (c: BuddyPose, side: 1 | -1, hand: Point): void => {
  c.arms.push({ side, hand });
};

export const hop = (c: BuddyPose, t: number, period: number, height: number): void => {
  const u = (t % period) / period;
  const air = u < 0.55 ? Math.sin((Math.PI * u) / 0.55) : 0;
  c.cy = -H2 - air * height;
  c.sy = 1 + 0.1 * air;
  c.sx = 1 - 0.06 * air;
  if (u > 0.55) {
    const landed = (u - 0.55) * period;
    const k = Math.exp(-landed * 8) * Math.cos(landed * 18);
    c.sy = 1 - 0.16 * k;
    c.sx = 1 + 0.1 * k;
  }
};

const roundedPath = (points: readonly Point[], radius: number): Path2D => {
  const path = new Path2D();
  points.forEach((p1, i) => {
    const p0 = points[(i - 1 + points.length) % points.length];
    const p2 = points[(i + 1) % points.length];
    const l1 = Math.hypot(p0[0] - p1[0], p0[1] - p1[1]);
    const l2 = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    const r = Math.min(radius, l1 / 2, l2 / 2);
    const ax = p1[0] + ((p0[0] - p1[0]) / l1) * r;
    const ay = p1[1] + ((p0[1] - p1[1]) / l1) * r;
    if (i === 0) path.moveTo(ax, ay);
    else path.lineTo(ax, ay);
    path.quadraticCurveTo(p1[0], p1[1], p1[0] + ((p2[0] - p1[0]) / l2) * r, p1[1] + ((p2[1] - p1[1]) / l2) * r);
  });
  path.closePath();
  return path;
};

let bodies: Record<BuddyKind, Path2D> | null = null;
const bodyOf = (kind: BuddyKind): Path2D => {
  bodies ??= {
    A: roundedPath(OUTLINES.A, CORNER_RADIUS.A),
    I: roundedPath(OUTLINES.I, CORNER_RADIUS.I),
  };
  return bodies[kind];
};

const drawArms = (g: CanvasRenderingContext2D, c: BuddyPose, colors: BuddyColors): void => {
  for (const arm of c.arms) {
    const shoulder = toWorld(c, [SHOULDER[c.kind][0] * arm.side, SHOULDER[c.kind][1]]);
    const [hx, hy] = arm.hand;
    const sag = Math.min(2.6, Math.hypot(hx - shoulder[0], hy - shoulder[1]) * 0.16);
    g.strokeStyle = colors.ink;
    g.lineWidth = 1.1;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(shoulder[0], shoulder[1]);
    g.quadraticCurveTo((shoulder[0] + hx) / 2, (shoulder[1] + hy) / 2 + sag, hx, hy);
    g.stroke();
    g.fillStyle = colors.ink;
    g.beginPath();
    g.arc(hx, hy, 1, 0, FULL_TURN);
    g.fill();
  }
};

const drawEye = (g: CanvasRenderingContext2D, c: BuddyPose, eye: Point, index: number, t: number, colors: BuddyColors): void => {
  const ex = eye[0] + c.look[0] * 0.3;
  const ey = eye[1] + c.look[1] * 0.28;
  g.lineCap = 'round';
  if (c.dizzy) {
    g.fillStyle = colors.face;
    g.beginPath();
    g.arc(ex, ey, 1.25, 0, FULL_TURN);
    g.fill();
    g.strokeStyle = colors.ink;
    g.lineWidth = 0.28;
    g.beginPath();
    for (let k = 0; k <= 24; k += 1) {
      const a = (k / 24) * Math.PI * 3.4 + t * 14 * (index ? 1 : -1);
      const r = 0.1 + (k / 24) * 0.95;
      if (k === 0) g.moveTo(ex + Math.cos(a) * r, ey + Math.sin(a) * r);
      else g.lineTo(ex + Math.cos(a) * r, ey + Math.sin(a) * r);
    }
    g.stroke();
  } else if (c.squint > 0.5) {
    g.strokeStyle = colors.face;
    g.lineWidth = 0.5;
    g.beginPath();
    g.moveTo(ex - 0.95, ey + 0.4);
    g.quadraticCurveTo(ex, ey - 0.9, ex + 0.95, ey + 0.4);
    g.stroke();
  } else if (c.open < 0.15) {
    g.strokeStyle = colors.face;
    g.lineWidth = 0.42;
    g.beginPath();
    g.moveTo(ex - 0.9, ey);
    g.quadraticCurveTo(ex, ey + 0.55, ex + 0.9, ey);
    g.stroke();
  } else {
    const ry = 1.5 * c.open;
    g.fillStyle = colors.face;
    g.beginPath();
    g.ellipse(ex, ey, 1.15, ry, 0, 0, FULL_TURN);
    g.fill();
    g.save();
    g.beginPath();
    g.ellipse(ex, ey, 1.15, ry, 0, 0, FULL_TURN);
    g.clip();
    const px = ex + c.look[0] * 0.5;
    const py = ey + c.look[1] * 0.6;
    g.fillStyle = colors.ink;
    g.beginPath();
    g.arc(px, py, 0.68, 0, FULL_TURN);
    g.fill();
    g.fillStyle = colors.face;
    g.beginPath();
    g.arc(px - 0.25, py - 0.28, 0.2, 0, FULL_TURN);
    g.fill();
    g.restore();
  }
};

const drawMouth = (g: CanvasRenderingContext2D, c: BuddyPose, colors: BuddyColors): void => {
  const [mx, my] = MOUTH[c.kind];
  g.strokeStyle = colors.face;
  g.fillStyle = colors.face;
  g.lineWidth = 0.38;
  g.lineCap = 'round';
  g.beginPath();
  switch (c.mouth) {
    case 'grin':
      g.moveTo(mx - 1, my - 0.1);
      g.quadraticCurveTo(mx, my + 1.7, mx + 1, my - 0.1);
      g.closePath();
      g.fill();
      return;
    case 'o':
      g.ellipse(mx, my + 0.25, 0.42, 0.55, 0, 0, FULL_TURN);
      g.fill();
      return;
    case 'flat':
      g.moveTo(mx - 0.6, my + 0.2);
      g.lineTo(mx + 0.6, my + 0.2);
      break;
    case 'effort':
      g.moveTo(mx - 0.8, my + 0.3);
      g.lineTo(mx - 0.4, my);
      g.lineTo(mx, my + 0.3);
      g.lineTo(mx + 0.4, my);
      g.lineTo(mx + 0.8, my + 0.3);
      break;
    case 'wobble':
      g.moveTo(mx - 0.8, my + 0.3);
      g.quadraticCurveTo(mx - 0.4, my - 0.2, mx, my + 0.3);
      g.quadraticCurveTo(mx + 0.4, my + 0.8, mx + 0.8, my + 0.3);
      break;
    default:
      g.moveTo(mx - 0.7, my);
      g.quadraticCurveTo(mx, my + 0.75, mx + 0.7, my);
  }
  g.stroke();
};

export const drawBuddy = (g: CanvasRenderingContext2D, c: BuddyPose, t: number, colors: BuddyColors): void => {
  drawArms(g, c, colors);
  g.save();
  g.translate(c.cx, c.cy);
  g.rotate(c.rot);
  g.translate(0, H2);
  g.scale(c.sx, c.sy);
  g.translate(0, -H2);
  g.fillStyle = colors.ink;
  g.fill(bodyOf(c.kind));
  const eyes = EYES[c.kind];
  if (c.blush > 0) {
    g.fillStyle = colors.blush;
    g.globalAlpha = c.blush * 0.85;
    eyes.forEach((e, i) => {
      g.beginPath();
      g.ellipse(e[0] + (i ? 0.9 : -0.9), e[1] + 2.2, 0.95, 0.48, 0, 0, FULL_TURN);
      g.fill();
    });
    g.globalAlpha = 1;
  }
  eyes.forEach((e, i) => drawEye(g, c, e, i, t, colors));
  drawMouth(g, c, colors);
  g.restore();
};

export const drawShadow = (g: CanvasRenderingContext2D, c: BuddyPose, colors: BuddyColors): void => {
  const foot = toWorld(c, [0, H2]);
  const k = clamp01(1 - Math.max(0, -foot[1]) / 26);
  if (k <= 0) return;
  g.fillStyle = colors.shadow;
  g.globalAlpha = k;
  g.beginPath();
  g.ellipse(foot[0], 0.15, BUDDY_HALF_WIDTH[c.kind] * (0.55 + 0.5 * k), 0.75, 0, 0, FULL_TURN);
  g.fill();
  g.globalAlpha = 1;
};

export const drawBulb = (g: CanvasRenderingContext2D, x: number, y: number, s: number, t: number, colors: BuddyColors): void => {
  g.save();
  g.translate(x, y);
  g.scale(s, s);
  g.strokeStyle = colors.spark;
  g.lineWidth = 0.45;
  g.lineCap = 'round';
  for (let k = 0; k < 8; k += 1) {
    const a = (k / 8) * FULL_TURN - Math.PI / 2;
    const r1 = 4.4 + 0.4 * Math.sin(t * 12 + k);
    g.beginPath();
    g.moveTo(Math.cos(a) * 3.1, Math.sin(a) * 3.1);
    g.lineTo(Math.cos(a) * r1, Math.sin(a) * r1);
    g.stroke();
  }
  g.fillStyle = colors.spark;
  g.beginPath();
  g.arc(0, -0.4, 2.2, 0, FULL_TURN);
  g.fill();
  g.fillStyle = colors.ink;
  g.fillRect(-1.1, 1.5, 2.2, 1.6);
  g.restore();
};

export const drawSparkle = (g: CanvasRenderingContext2D, x: number, y: number, s: number, color: string): void => {
  g.save();
  g.translate(x, y);
  g.scale(s, s);
  g.fillStyle = color;
  g.beginPath();
  for (let k = 0; k < 8; k += 1) {
    const a = (k / 8) * FULL_TURN;
    const r = k % 2 ? 0.35 : 1.4;
    if (k === 0) g.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.closePath();
  g.fill();
  g.restore();
};

export const drawSleepZs = (g: CanvasRenderingContext2D, x: number, y: number, t: number, colors: BuddyColors): void => {
  g.fillStyle = colors.muted;
  g.font = '600 3.2px Inter, sans-serif';
  g.textAlign = 'center';
  for (let k = 0; k < 3; k += 1) {
    const ph = (t * 0.5 + k / 3) % 1;
    g.globalAlpha = 1 - ph;
    g.fillText('z', x + 2 + ph * 4 + k * 1.2, y - 4 - ph * 7 - k * 1.5);
  }
  g.globalAlpha = 1;
};

export const drawQuestionMark = (g: CanvasRenderingContext2D, x: number, y: number, s: number, alpha: number, colors: BuddyColors): void => {
  g.save();
  g.translate(x, y);
  g.scale(s, s);
  g.globalAlpha = alpha;
  g.fillStyle = colors.spark;
  g.font = '800 6px Inter, sans-serif';
  g.textAlign = 'center';
  g.fillText('?', 0, 0);
  g.restore();
  g.globalAlpha = 1;
};
